import { useQuery } from '@tanstack/react-query';

import type { GetMarkBackfillInventoryAddedToInventorySystemResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to get backfill inventory data from a purchased vehicle
 */
export const getMarkBackfillInventoryAddedToInventorySystem = async (
	servicesProviderId: string,
	orderId: string,
): Promise<GetMarkBackfillInventoryAddedToInventorySystemResponse> => {
	const response = await apiClient.get<GetMarkBackfillInventoryAddedToInventorySystemResponse>(
		ordersEndpoints.markBackfillInventoryAddedToInventorySystem(servicesProviderId, orderId),
	);
	return response.data;
};

/**
 * Hook for getting backfill inventory data from a purchased vehicle
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useGetMarkBackfillInventoryAddedToInventorySystem = (orderId: string | undefined) => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.orders.detail(servicesProviderId, orderId || '')._ctx.progress._ctx
			.markBackfillInventoryAddedToInventorySystem.queryKey,
		queryFn: () => {
			if (!orderId) {
				throw new Error('Unable to get backfill inventory data without an order ID.');
			}
			return getMarkBackfillInventoryAddedToInventorySystem(servicesProviderId, orderId);
		},
		enabled: Boolean(orderId) && Boolean(servicesProviderId),
		staleTime: Infinity,
		...errorBoundaryOptions,
	});
};
